import { blake3 } from "@noble/hashes/blake3.js";

const CONTENT_ID_LENGTH = 20; // 80 bits

// Type tags to ensure different JSON types don't collide
const TYPE_NULL = 0;
const TYPE_BOOL = 1;
const TYPE_NUMBER = 2;
const TYPE_STRING = 3;
const TYPE_ARRAY = 4;
const TYPE_OBJECT = 5;

type Blake3 = ReturnType<typeof blake3.create>;

type JsonValue =
  | null
  | boolean
  | number
  | string
  | JsonValue[]
  | { [key: string]: JsonValue };

const encoder = new TextEncoder();

function hashJsonValue(hash: Blake3, value: JsonValue): void {
  if (value === undefined) {
    throw new Error("contentId received undefined");
  }
  if (value === null) {
    hash.update(new Uint8Array([TYPE_NULL]));
  } else if (typeof value === "boolean") {
    hash.update(new Uint8Array([TYPE_BOOL, value ? 1 : 0]));
  } else if (typeof value === "number") {
    hash.update(new Uint8Array([TYPE_NUMBER]));
    // Use JSON.stringify to get a canonical string representation of the number
    const numBytes = encoder.encode(JSON.stringify(value));
    hashLengthPrefixed(hash, numBytes);
  } else if (typeof value === "string") {
    hash.update(new Uint8Array([TYPE_STRING]));
    hashLengthPrefixed(hash, encoder.encode(value));
  } else if (Array.isArray(value)) {
    hash.update(new Uint8Array([TYPE_ARRAY]));
    // Write array length
    const lenBuf = new Uint8Array(4);
    new DataView(lenBuf.buffer).setUint32(0, value.length, false);
    hash.update(lenBuf);
    // Hash each element in order
    for (const item of value) {
      hashJsonValue(hash, item);
    }
  } else {
    if (typeof value !== "object") {
      throw new Error(`contentId received unsupported type: ${typeof value}`);
    }
    // Object
    hash.update(new Uint8Array([TYPE_OBJECT]));
    const sortedKeys = Object.keys(value).sort();
    // Write number of keys
    const lenBuf = new Uint8Array(4);
    new DataView(lenBuf.buffer).setUint32(0, sortedKeys.length, false);
    hash.update(lenBuf);
    // Hash each key-value pair
    for (const key of sortedKeys) {
      hashLengthPrefixed(hash, encoder.encode(key));
      const nestedValue = value[key];
      if (nestedValue === undefined) {
        throw new Error(`contentId object key ${key} is undefined`);
      }
      hashJsonValue(hash, nestedValue);
    }
  }
}

function hashLengthPrefixed(hash: Blake3, bytes: Uint8Array): void {
  const lenBuf = new Uint8Array(4);
  new DataView(lenBuf.buffer).setUint32(0, bytes.length, false);
  hash.update(lenBuf);
  hash.update(bytes);
}

export function contentId(value: JsonValue): string {
  const hash = blake3.create({});
  hashJsonValue(hash, value);
  const bytes = hash.digest();
  // Convert Uint8Array to hex string (browser-compatible)
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hex.slice(0, CONTENT_ID_LENGTH);
}
